import { parseUsInventoryFills, usFillProgress, type UsInventoryFill } from './fillProgress';
import { parseUsClientOrder, UsClientError, type UsClientScope, type UsClientSession } from './tradingClient';
import { isSameUsScope } from './portfolioModel';
import type { UsCancelAttempt, UsCancelRequest, UsCancelResult, UsCoverage, UsOrderRow, UsOrderStatusRequest,
  UsPortfolioAdapter, UsPortfolioSnapshot, UsPositionRow, UsReceipt, UsScope } from './types';

/** #1034 — the venue-reported cash for one account, as last saved by a
 * complete relay scan. Like Kalshi's balance, G1 applies no hold math: this is
 * never an available-after-orders figure or a member's share of a pooled
 * account. Saved balances are not refreshed on a timer; `observedAtMs` says when. */
export interface UsSavedBalance {
  currentBalance: string;
  /** Venue time of the last balance change. */
  updatedAt: string | null;
  /** When G1 saved this reading. */
  observedAtMs: number;
}

export interface UsPortfolioClientDependencies {
  session(): UsClientSession | null;
  send(path: string, init: RequestInit, session: UsClientSession): Promise<Response>;
  uuid(): string;
  clock(): number;
}
const fail = (code = 'US_CLIENT_INVALID_RESPONSE'): never => { throw new UsClientError(code); };
const object = (v: unknown): Record<string, unknown> => v && typeof v === 'object' && !Array.isArray(v)
  ? v as Record<string, unknown> : fail();
const uuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v);
const text = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 256 && !/[\u0000-\u001f\u007f]/.test(v);
const time = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) >= 0;
const reasons = (v: unknown): string[] => Array.isArray(v) && v.length <= 64
  && v.every(r => typeof r === 'string' && /^[a-zA-Z0-9_]{1,120}$/.test(r)) ? [...v] : fail();
const sameSession = (a: UsClientSession | null, b: UsClientSession) => a?.userId === b.userId
  && a.orgId === b.orgId && a.generation === b.generation;

const quantity = (value: unknown): string => typeof value === 'string'
  && /^(?:0|[1-9]\d{0,23})(?:\.\d{1,12})?$/.test(value) ? value : fail();
const units = (value: string): bigint => {
  const [whole, fraction = ''] = value.replace(/^-/, '').split('.');
  return (BigInt(whole) * 1_000_000_000_000n + BigInt(fraction.padEnd(12, '0'))) * (value.startsWith('-') ? -1n : 1n);
};
function allocation(row: Record<string, unknown>, recorded: boolean): UsPositionRow['allocation'] {
  if (!recorded) return undefined;
  const openingQuantity = quantity(row.openingQuantity);
  // The server omits settledQuantity until an explicit settlement is applied.
  const settledQuantity = row.settledQuantity === undefined ? '0' : quantity(row.settledQuantity);
  const known = row.knownAllocatedQuantity === null ? null : quantity(row.knownAllocatedQuantity);
  // Sales beyond the trader's own holdings close only those holdings (the K/P
  // per-user position fold); the server reports the excess separately.
  const unownedSold = row.unownedSoldQuantity === undefined ? '0' : quantity(row.unownedSoldQuantity);
  if (known !== null && units(known) !== units(openingQuantity) + units(row.knownClearedNetQuantity as string)
    + units(unownedSold) - units(settledQuantity)) return fail();
  return { openingQuantity, settledQuantity,
    knownQuantity: row.localHistoryComplete === true && reasons(row.reasons).length === 0 ? known : null };
}

/** One saved own-inventory read (the relay's `UsOwnedInventory`) as position
 * rows and holdings coverage. `expected` is the account and trader the read
 * was asked for; any mismatch or malformed row refuses the whole read. Shared
 * by the tracker's own read and manager trader detail (#1023). */
export function mapUsInventory(raw: unknown, expected: { accountId: string; userId: string },
  scope: UsScope): { positions: UsPositionRow[]; coverage: UsCoverage; orderFills: readonly UsInventoryFill[] } {
  const inventory = object(raw);
  if (inventory.accountId !== expected.accountId || inventory.userId !== expected.userId
    || !['unverified', 'recorded'].includes(String(inventory.openingAllocation)) || inventory.accountConserved !== false
    || inventory.riskReleasable !== false || typeof inventory.localHistoryComplete !== 'boolean'
    || !Array.isArray(inventory.rows) || inventory.rows.length > 10_000) return fail();
  const ledger = inventory.basis === 'g1_ledger' && time(inventory.readAtMs);
  const openingReason = inventory.openingAllocation === 'recorded' ? 'US_ACCOUNT_CONSERVATION_UNVERIFIED' : 'US_OPENING_ALLOCATION_UNVERIFIED';
  const missing = [...reasons(inventory.reasons), openingReason, 'US_ACCOUNT_NOT_CONSERVED'];
  const positions: UsPositionRow[] = inventory.rows.map(value => {
    const row = object(value);
    if (!text(row.marketSlug) || !['yes','no'].includes(String(row.outcome)) || row.availableToSell !== null
      || typeof row.localHistoryComplete !== 'boolean' || typeof row.knownClearedNetQuantity !== 'string'
      || !/^-?(?:0|[1-9]\d{0,23})(?:\.\d{1,12})?$/.test(row.knownClearedNetQuantity)) return fail();
    let owned: UsPositionRow['allocation'];
    const allocationReasons: string[] = [];
    try { owned = allocation(row, inventory.openingAllocation === 'recorded'); }
    catch { allocationReasons.push('US_ALLOCATION_INVALID_RESPONSE'); }
    return { scope: { ...scope }, target: { marketSlug: row.marketSlug, outcome: row.outcome as 'yes' | 'no',
      label: `${row.marketSlug} — ${String(row.outcome).toUpperCase()}` },
    heldQuantity: ledger ? owned?.knownQuantity ?? null : null,
    knownContribution: row.knownClearedNetQuantity,
    quantityBasis: ledger ? 'g1_ledger' as const : 'local_cleared_contribution' as const,
    ...(ledger ? { ledgerReadAtMs: inventory.readAtMs as number } : {}),
    // Absent from an older relay: Close then falls back to the orders on screen.
    // A malformed count is no count (null), never a reason to hide the read.
    ...(row.openOwnSellQuantity !== undefined ? { openOwnSellQuantity: typeof row.openOwnSellQuantity === 'string'
      && /^(?:0|[1-9]\d{0,23})(?:\.\d{1,12})?$/.test(row.openOwnSellQuantity) ? row.openOwnSellQuantity : null } : {}),
    ...(owned ? { allocation: owned } : {}),
    feesUsd: null, observedAtMs: null, reasons: [...new Set([
      ...(ledger ? (owned?.knownQuantity != null ? [] : ['US_LEDGER_HOLDING_UNAVAILABLE']) : missing),
      ...reasons(row.reasons), ...allocationReasons,
      ...(ledger ? [] : ['US_INVENTORY_OBSERVATION_UNAVAILABLE'])])] };
  });
  if (new Set(positions.map(r => JSON.stringify([r.target.marketSlug,r.target.outcome]))).size !== positions.length) return fail();
  // A recorded opening with no rows is a complete read of an empty ledger (the
  // worker's first complete venue read records an empty opening for a fresh
  // account, docs/260928PolymarketUsOpeningAllocation.md). Until that opening
  // exists the relay withholds the basis, and the reason says so (#1230): an
  // opening not yet recorded is not an incomplete history.
  const coverage: UsCoverage = ledger && inventory.openingAllocation === 'recorded'
    && inventory.localHistoryComplete && positions.every(p => p.heldQuantity !== null && p.reasons.length === 0)
    ? { state: 'complete', observedAtMs: inventory.readAtMs as number }
    : { state: 'incomplete', observedAtMs: null,
      reason: ledger && inventory.openingAllocation === 'recorded' ? 'US_LEDGER_HOLDINGS_INCOMPLETE' : openingReason };
  return { positions, coverage, orderFills: parseUsInventoryFills(inventory.orderFills) };
}

function cancelAttempt(v: unknown): UsCancelAttempt | null {
  if (v === null) return null;
  const r = object(v);
  if (!uuid(r.attemptId) || !['dispatching','acknowledged','unknown','not_sent'].includes(String(r.state))
    || !time(r.requestedAtMs) || !Number.isSafeInteger(r.generation) || Number(r.generation) < 0) return fail();
  return { attemptId: r.attemptId, state: r.state as UsCancelAttempt['state'],
    requestedAtMs: r.requestedAtMs, generation: Number(r.generation) };
}

function recordedFees(value: unknown, life: ReturnType<typeof parseUsClientOrder>['lifecycle']):
  Pick<UsOrderRow, 'feesUsd' | 'feeEvidence'> {
  if (value === undefined) return { feesUsd: null };
  try {
    const r = object(value);
    if (r.source !== 'recorded_executions') return fail();
    const filled = r.filledQuantity === null ? null : quantity(r.filledQuantity);
    const observedAtMs = r.observedAtMs === null ? null : time(r.observedAtMs) ? r.observedAtMs : fail();
    const why = reasons(r.reasons);
    const fee = r.feesUsd === null ? null : typeof r.feesUsd === 'string'
      && /^-?(?:0|[1-9]\d{0,27})(?:\.\d{1,12})?$/.test(r.feesUsd) ? r.feesUsd : fail();
    if (fee !== null && (why.length || !life || filled === null || observedAtMs === null
      || units(filled) !== units(life.filledQuantity) || observedAtMs < life.observedAtMs
      || life.state === 'conflict' || life.state === 'replaced')) return fail();
    return { feesUsd: fee, feeEvidence: { filledQuantity: filled, observedAtMs, reasons: why } };
  } catch {
    // A bad optional fee never erases the order, lifecycle or cancel recovery.
    return { feesUsd: null, feeEvidence: { filledQuantity: null, observedAtMs: null,
      reasons: ['execution_evidence_invalid'] } };
  }
}

/** Explicit wire-to-presenter boundary. An order ID is not acceptance; fee
 * reserves are not paid fees; server timestamps are not browser receipt time. */
export function mapUsPortfolioOrder(value: unknown, accountId: string, scope: UsScope, view: 'own' | 'desk'): UsOrderRow {
  const r = object(value), parsed = parseUsClientOrder({ venue: 'polymarket_us', source: 'saved_evidence', order: r }, accountId, view);
  const id = parsed.intentId;
  let receipt: UsReceipt;
  switch (parsed.submissionState) {
    case 'ready': receipt = { status: 'ready', intentId: id }; break;
    case 'aborted': case 'rejected': receipt = { status: parsed.submissionState, intentId: id,
      reason: parsed.reason ?? 'US_REASON_UNAVAILABLE' }; break;
    case 'accepted': case 'submitted':
      if (!parsed.orderId) return fail();
      receipt = { status: parsed.submissionState, intentId: id, orderId: parsed.orderId }; break;
    default: receipt = { status: 'unknown', intentId: id, reason: parsed.reason ?? 'US_ACCEPTANCE_UNCONFIRMED' };
  }
  const mapped: UsOrderRow = { scope: { ...scope }, intentId: id, orderId: parsed.orderId, target: { ...parsed.target, marketTitle: parsed.marketTitle,
    // #1026: two markets on one game can share a title, so the slug stays beside it.
    label: parsed.marketTitle === null ? `${parsed.target.marketSlug} — ${parsed.target.outcome.toUpperCase()}`
      : `${parsed.marketTitle} — ${parsed.target.outcome.toUpperCase()} · ${parsed.target.marketSlug}` },
  action: parsed.action, ownership: parsed.own ? 'own' : 'desk', quantity: parsed.quantity,
  outcomePrice: parsed.outcomePrice, timeInForce: parsed.timeInForce, postOnly: parsed.postOnly,
  goodTillTime: parsed.goodTillTime, createdAtMs: parsed.createdAtMs, receipt, lifecycle: parsed.lifecycle ? {
    state: parsed.lifecycle.state as NonNullable<UsOrderRow['lifecycle']>['state'],
    filledQuantity: parsed.lifecycle.filledQuantity, remainingQuantity: parsed.lifecycle.remainingQuantity,
    observedAtMs: parsed.lifecycle.observedAtMs } : null,
  ...recordedFees(r.executionFees, parsed.lifecycle), cancel: cancelAttempt(r.cancel),
  observedAtMs: Math.max(parsed.updatedAtMs, parsed.lifecycle?.observedAtMs ?? 0) };
  return mapped;
}

/** One mounted portfolio, with explicit selection and no polling. Every scope
 * visit and new operation invalidates earlier read results, including A-B-A.
 * Saved G1 history never certifies full venue holdings or execution readiness. */
export class UsPortfolioClient implements UsPortfolioAdapter {
  private selection: { accountId: string; scope: UsScope; view: 'own' | 'desk'; session: UsClientSession } | null = null;
  private visit = 0;
  private operation = 0;
  private cancellation: { key: string; promise: Promise<UsCancelResult> } | null = null;
  constructor(private readonly d: UsPortfolioClientDependencies) {}

  setSelection(value: (UsClientScope & { view: 'own' | 'desk' }) | null): void {
    this.visit++; this.operation++; this.selection = null; this.cancellation = null;
    if (!value) return;
    if (!uuid(value.accountId) || !text(value.scopeKey) || !text(value.revision)
      || !['own','desk'].includes(value.view)) fail('US_CLIENT_INVALID_SCOPE');
    const session = this.d.session();
    if (!session || !uuid(session.userId) || !uuid(session.orgId) || !time(session.generation)) return fail('US_CLIENT_AUTH_REQUIRED');
    this.selection = { accountId: value.accountId, scope: { venue: 'polymarket_us', scopeKey: value.scopeKey,
      revision: value.revision }, view: value.view, session: { ...session } };
  }
  private context(scope: UsScope) {
    const s = this.selection;
    if (!s || !isSameUsScope(scope, s.scope)) return fail('US_CLIENT_CONTEXT_CHANGED');
    if (!sameSession(this.d.session(), s.session)) return fail('US_CLIENT_CONTEXT_CHANGED');
    return { ...s, scope: { ...s.scope }, session: { ...s.session }, visit: this.visit, operation: ++this.operation };
  }
  private current(c: ReturnType<UsPortfolioClient['context']>): void {
    if (this.visit !== c.visit || this.operation !== c.operation || !sameSession(this.d.session(), c.session)) {
      fail('US_CLIENT_CONTEXT_CHANGED');
    }
  }
  private async request(c: ReturnType<UsPortfolioClient['context']>, path: string, body?: unknown): Promise<Record<string, unknown>> {
    this.current(c);
    let response: Response;
    try {
      response = await this.d.send(`/api/polymarket-us/accounts/${c.accountId}${path}`, {
        method: body === undefined ? 'GET' : 'POST', cache: 'no-store', redirect: 'error',
        headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(15_000),
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }, c.session);
    } catch { this.current(c); return fail(body === undefined ? 'US_READ_UNAVAILABLE' : 'US_CANCEL_UNCONFIRMED'); }
    this.current(c);
    let data: Record<string, unknown>;
    try { data = object(await response.json()); } catch { this.current(c); return fail(); }
    this.current(c);
    if (!response.ok) return fail(typeof data.code === 'string' && /^(US|RELAY)_[A-Z0-9_]{1,100}$/.test(data.code)
      ? data.code : body === undefined ? 'US_READ_UNAVAILABLE' : 'US_CANCEL_UNCONFIRMED');
    if (data.venue !== 'polymarket_us') return fail();
    if (body === undefined && data.source !== 'saved_evidence') return fail();
    return data;
  }
  private async order(c: ReturnType<UsPortfolioClient['context']>, intentId: string): Promise<UsOrderRow> {
    if (!uuid(intentId)) return fail('US_CLIENT_INVALID_REQUEST');
    const data = await this.request(c, `/orders/${intentId}`);
    const row = mapUsPortfolioOrder(data.order, c.accountId, c.scope, c.view);
    if (row.intentId !== intentId) return fail();
    return row;
  }
  async checkOrderStatus(request: UsOrderStatusRequest): Promise<UsOrderRow> {
    if (this.cancellation) return fail('US_CLIENT_CANCEL_PENDING');
    return this.order(this.context(request.scope), request.intentId);
  }
  async readPortfolio(scope: UsScope): Promise<UsPortfolioSnapshot> {
    return this.readOrderPage(scope);
  }
  /** Explicit pagination only. Each page replaces the page shown, never claims
   * that several concurrent page reads were an atomic account snapshot. */
  async readOrderPage(scope: UsScope, after?: string): Promise<UsPortfolioSnapshot> {
    if (this.cancellation) return fail('US_CLIENT_CANCEL_PENDING');
    if (after !== undefined && !/^[A-Za-z0-9_-]{1,160}$/.test(after)) return fail('US_CLIENT_INVALID_REQUEST');
    const c = this.context(scope);
    const [orderResult, inventoryResult] = await Promise.allSettled([
      this.request(c, `/orders?view=${c.view}${after ? '&after=' + encodeURIComponent(after) : ''}`),
      c.view === 'own' ? this.request(c, '/inventory') : Promise.reject(new UsClientError('US_DESK_INVENTORY_UNAVAILABLE')),
    ]);
    this.current(c);
    if (orderResult.status === 'rejected') throw orderResult.reason;
    const page = orderResult.value;
    if (page.accountId !== c.accountId || !Array.isArray(page.orders) || page.orders.length > 100
      || !(page.nextCursor === null || (typeof page.nextCursor === 'string' && /^[A-Za-z0-9_-]{1,160}$/.test(page.nextCursor)))) return fail();
    const orders = page.orders.map(row => mapUsPortfolioOrder(row, c.accountId, c.scope, c.view));
    if (new Set(orders.map(r => r.intentId)).size !== orders.length) return fail();
    // No venue snapshot/freshness claim: even a final saved page excludes
    // outside-G1 orders and cannot establish a complete live order total.
    const receivedAtMs = this.d.clock();
    const orderCoverage: UsCoverage = { state: 'incomplete', observedAtMs: orders.length ? Math.max(...orders.map(r => r.observedAtMs)) : null,
      reason: page.nextCursor !== null || after ? 'US_SAVED_ORDER_PAGE' : 'US_SAVED_ORDERS_ONLY' };
    let positions: UsPositionRow[] = [];
    let orderFills: readonly UsInventoryFill[] = [];
    let inventoryCoverage: UsCoverage = { state: 'unavailable', reason: c.view === 'desk' ? 'US_DESK_INVENTORY_UNAVAILABLE' : 'US_INVENTORY_UNAVAILABLE' };
    if (inventoryResult.status === 'fulfilled') {
      ({ positions, coverage: inventoryCoverage, orderFills } = mapUsInventory(inventoryResult.value.inventory,
        { accountId: c.accountId, userId: c.session.userId }, c.scope));
    }
    return { scope: c.scope, readiness: { state: 'unavailable', reason: 'US_EXECUTION_READINESS_NOT_PROVIDED' },
      coverage: { state: 'incomplete', observedAtMs: orderCoverage.observedAtMs, reason: 'US_SAVED_EVIDENCE_ONLY' },
      orders: c.view === 'own' ? orders.map(order => ({ ...order, fillProgress: usFillProgress(order, orderFills,
        positions.some(p => p.target.marketSlug === order.target.marketSlug && p.target.outcome === order.target.outcome
          && p.heldQuantity !== null && p.reasons.length === 0)) })) : orders,
      positions, readDetails: { view: c.view, nextOrderCursor: page.nextCursor as string | null,
        receivedAtMs, orders: orderCoverage, inventory: inventoryCoverage, ...(c.view === 'own' ? { orderFills } : {}) } };
  }
  /** The selected account's saved balance; null when none is saved yet. */
  async readBalance(scope: UsScope): Promise<UsSavedBalance | null> {
    if (this.cancellation) return fail('US_CLIENT_CANCEL_PENDING');
    const c = this.context(scope);
    const data = await this.request(c, '/balance');
    if (data.balance === null) return null;
    const b = object(data.balance);
    if (b.currency !== 'USD' || typeof b.currentBalance !== 'string'
      || !/^-?(?:0|[1-9]\d{0,23})(?:\.\d{0,11}[1-9])?$/.test(b.currentBalance) || b.currentBalance === '-0'
      || (b.updatedAt !== null && (typeof b.updatedAt !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/.test(b.updatedAt)))
      || !time(b.observedAtMs)) return fail();
    return { currentBalance: b.currentBalance, updatedAt: b.updatedAt, observedAtMs: b.observedAtMs };
  }
  cancelOrder(request: UsCancelRequest): Promise<UsCancelResult> { return this.cancel(request); }
  repeatCancel(request: UsCancelRequest & { previousAttemptId: string }): Promise<UsCancelResult> {
    return this.cancel(request, request.previousAttemptId);
  }
  private cancel(request: UsCancelRequest, previousAttemptId?: string): Promise<UsCancelResult> {
    const key = JSON.stringify([request.scope, request.intentId, request.orderId, previousAttemptId]);
    if (this.cancellation) return this.cancellation.key === key ? this.cancellation.promise : Promise.reject(new UsClientError('US_CLIENT_CANCEL_PENDING'));
    const run = async () => {
      // Everything before the cancellation POST: a failure here provably sent
      // nothing, and is marked so a caller need not call it unconfirmed (#1006).
      let c: ReturnType<UsPortfolioClient['context']>, before: UsOrderRow, requestKey: string;
      try {
        if (!uuid(request.intentId) || !text(request.orderId) || (previousAttemptId !== undefined && !uuid(previousAttemptId))) fail('US_CLIENT_INVALID_REQUEST');
        c = this.context(request.scope); before = await this.order(c, request.intentId);
        if (before.orderId !== request.orderId) fail('US_CLIENT_ORDER_CHANGED');
        requestKey = this.d.uuid(); if (!uuid(requestKey)) fail('US_CLIENT_INVALID_REQUEST');
      } catch (error) {
        throw Object.assign(error instanceof UsClientError ? error : new UsClientError('US_READ_UNAVAILABLE'), { cancelNotSent: true as const });
      }
      const result = await this.request(c, `/orders/${before.intentId}/cancellations${previousAttemptId ? '/recover' : ''}`,
        previousAttemptId ? { requestKey, previousCancelId: previousAttemptId } : { requestKey });
      const receipt = object(result.cancellation);
      if (result.accountId !== c.accountId || result.intentId !== before.intentId || receipt.intentId !== before.intentId
        || !uuid(receipt.id) || !['dispatching','acknowledged','unknown','not_sent'].includes(String(receipt.state))
        || !['confirmed','unavailable','not_requested'].includes(String(result.readback))) return fail();
      // Reload the public projection, including the authoritative active cancel
      // generation. A failed read never causes another cancellation POST.
      const order = await this.order(c, before.intentId);
      if (!order.cancel) return fail('US_CANCEL_UNCONFIRMED');
      return { scope: c.scope, intentId: before.intentId, attempt: order.cancel, order,
        readback: result.readback as UsCancelResult['readback'] };
    };
    const pending = { key, promise: run() };
    pending.promise = pending.promise.finally(() => { if (this.cancellation === pending) this.cancellation = null; });
    this.cancellation = pending;
    return pending.promise;
  }
}
